(() => {
  const config = window.YB_AUTO_LOCALIZATION || {};
  const normalizeLocaleCode = (code) => String(code || '').trim().replace(/_/g, '-').toLowerCase();
  const currentCountry = String(config.currentCountry || '').toUpperCase();
  const currentLanguage = normalizeLocaleCode(config.currentLanguage);
  const currentCurrency = String(config.currentCurrency || '').toUpperCase();
  const availableCountries = new Set(
    (config.availableCountries || []).map((code) => String(code).toUpperCase()).filter((code) => /^[A-Z]{2}$/.test(code))
  );
  const availableLanguages = new Map(
    (config.availableLanguages || [])
      .map((code) => [normalizeLocaleCode(code), String(code)])
      .filter(([code]) => /^[a-z]{2,3}(?:-[a-z0-9]{2,8})*$/.test(code))
  );
  const manualChoiceKey = 'yb-localization-manual-choice';
  const manualCountryKey = 'yb-localization-country';
  const manualLanguageKey = 'yb-localization-language';
  const manualAttemptedKey = 'yb-manual-localization-attempted';
  const attemptedKey = 'yb-auto-localization-attempted';

  const getStorageValue = (storageName, key) => {
    try {
      return window[storageName].getItem(key);
    } catch (error) {
      return null;
    }
  };

  const getCookieValue = (key) => {
    try {
      const prefix = `${key}=`;
      const cookie = document.cookie.split(';').map((item) => item.trim()).find((item) => item.startsWith(prefix));
      return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
    } catch (error) {
      return null;
    }
  };

  const setSessionValue = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      try {
        document.cookie = `${key}=${encodeURIComponent(value)}; Path=/; SameSite=Lax`;
      } catch (cookieError) {
        return;
      }
    }
  };

  const getSessionValue = (key) => getStorageValue('sessionStorage', key) || getCookieValue(key);
  const getSavedValue = (key) => getStorageValue('localStorage', key) || getCookieValue(key);
  const hasManualChoice = () => getSavedValue(manualChoiceKey) === 'true';
  const isRegionDialogOpen = () => Boolean(document.querySelector('.yb-region-selector__dialog:not([hidden])'));
  let purchaseInteractionStarted = false;
  const purchaseSelector = [
    'product-form', '.product-form', 'variant-selects', 'quantity-input', '.yeahbox-mobile-buy-bar',
    'cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification',
    '.shopify-payment-button', 'shopify-accelerated-checkout', 'shopify-accelerated-checkout-cart',
    'form[action*="/cart"]', 'form[action*="/checkout"]', '[name="checkout"]',
    'a[href*="/cart"]', 'a[href*="/checkout"]',
  ].join(', ');
  const protectPurchaseInteraction = (event) => {
    if (!event.target?.closest?.(purchaseSelector)) return;
    purchaseInteractionStarted = true;
    setSessionValue(attemptedKey, 'shopping');
  };
  ['pointerdown', 'keydown', 'click', 'input', 'change', 'submit'].forEach((eventName) => {
    document.addEventListener(eventName, protectPurchaseInteraction, true);
  });
  const isShoppingActive = () => purchaseInteractionStarted || getSessionValue(attemptedKey) === 'shopping' ||
    /(?:^|\/)(?:cart|checkout|checkouts)(?:\/|$)/.test(window.location.pathname || '') ||
    Boolean(document.querySelector('cart-drawer.active, #cart-notification.active, product-form .loading'));

  const matchAvailableLanguage = (preferredLanguage) => {
    const normalized = normalizeLocaleCode(preferredLanguage);
    if (!normalized) return '';
    if (availableLanguages.has(normalized)) return availableLanguages.get(normalized);
    const base = normalized.split('-')[0];
    // Shopify uses /zh for Simplified Chinese; script/region aliases must not
    // cross the Simplified/Traditional boundary when both are published.
    if (base === 'zh') {
      const traditional = /(?:^|-)(?:hant|tw|hk|mo)(?:-|$)/.test(normalized);
      const aliases = traditional ? ['zh-tw', 'zh-hant'] : ['zh-cn', 'zh-hans', 'zh'];
      for (const alias of aliases) {
        if (availableLanguages.has(alias)) return availableLanguages.get(alias);
      }
      return '';
    }
    if (availableLanguages.has(base)) return availableLanguages.get(base);
    const candidates = [...availableLanguages.keys()].filter((code) => code.split('-')[0] === base);
    return candidates.length === 1 ? availableLanguages.get(candidates[0]) : '';
  };

  // Language is selected from IP geography, independently of Markets currency.
  // Never infer language from navigator.languages or the current market when
  // detection fails: those were the source of US visitors seeing Chinese.
  const getCountryLanguage = (countryCode) => {
    const languageByCountry = {
      US: 'en', GB: 'en', AU: 'en', DE: 'de', FR: 'fr', IT: 'it',
      JP: 'ja', ES: 'es', CN: 'zh-CN', HK: 'zh-TW', MO: 'zh-TW', TW: 'zh-TW',
    };
    return matchAvailableLanguage(languageByCountry[countryCode] || 'en') || matchAvailableLanguage('en');
  };

  const isExplicitLocaleSegment = (segment) => {
    const normalized = normalizeLocaleCode(segment);
    const routeCodes = new Set(availableLanguages.keys());
    if (['zh', 'zh-cn', 'zh-hans'].some((code) => availableLanguages.has(code))) {
      ['zh', 'zh-cn', 'zh-hans'].forEach((code) => routeCodes.add(code));
    }
    if (['zh-tw', 'zh-hant'].some((code) => availableLanguages.has(code))) {
      ['zh-tw', 'zh-hant'].forEach((code) => routeCodes.add(code));
    }
    if (routeCodes.has(normalized)) return true;
    if (['zh', 'zh-cn', 'zh-hans', 'zh-tw', 'zh-hant'].includes(normalized)) return false;
    const baseMatches = [...availableLanguages.keys()].filter((code) => code.split('-')[0] === normalized);
    if (normalized !== 'zh' && baseMatches.length === 1) return true;
    return [...routeCodes].some((code) => {
      const suffix = normalized.startsWith(`${code}-`) ? normalized.slice(code.length + 1) : '';
      return /^[a-z]{2}$/.test(suffix) && availableCountries.has(suffix.toUpperCase());
    });
  };

  const firstPathSegment = (path) => String(path || '').split('?')[0].split('#')[0].split('/').filter(Boolean)[0] || '';
  const hasExplicitLocalePath = () =>
    isExplicitLocaleSegment(firstPathSegment(window.location.pathname)) ||
    isExplicitLocaleSegment(firstPathSegment(window.Shopify?.routes?.root));

  const getReturnTo = (countryCode, languageCode) => {
    const pathname = window.location.pathname || '/';
    const changedLocale = (countryCode && countryCode !== currentCountry) ||
      (languageCode && normalizeLocaleCode(languageCode) !== currentLanguage);
    const shouldStripLocale = changedLocale && isExplicitLocaleSegment(firstPathSegment(pathname));
    const returnPath = shouldStripLocale ? pathname.replace(/^\/[^/]+/, '') || '/' : pathname;
    const query = String(window.location.search || '').replace(/^\?/, '').split('&').filter((part) => {
      const key = part.split('=', 1)[0];
      try {
        // Remove stale market hints only when the country is actually changed.
        return !countryCode || countryCode === currentCountry ||
          !['country', 'currency'].includes(decodeURIComponent(key.replace(/\+/g, ' ')).toLowerCase());
      } catch (error) {
        return true;
      }
    }).join('&');
    return `${returnPath}${query ? `?${query}` : ''}${window.location.hash || ''}`;
  };

  const appendHiddenField = (form, name, value) => {
    const field = document.createElement('input');
    field.type = 'hidden';
    field.name = name;
    field.value = value;
    form.appendChild(field);
  };

  // Automatic localization never changes an existing basket. Manual country
  // changes use the explicit selector's delivery/availability preflight.
  const cartIsEmpty = async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root.replace(/\/$/, '')}/cart.js`, {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal,
      });
      if (!response.ok) return false;
      const cart = await response.json();
      return Number.isSafeInteger(cart.item_count) && cart.item_count === 0 && Array.isArray(cart.items) && cart.items.length === 0;
    } catch (error) {
      return false;
    } finally {
      clearTimeout(timeout);
    }
  };

  const countryHasDelivery = async (countryCode) => {
    const selector = document.querySelector('yb-region-selector[data-delivery-eligibility-url]');
    const url = selector?.dataset.deliveryEligibilityUrl;
    if (!url) return false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    try {
      const response = await fetch(url, { credentials: 'omit', headers: { Accept: 'application/json' }, signal: controller.signal });
      if (!response.ok) return false;
      const delivery = await response.json();
      return delivery.version === 1 && Array.isArray(delivery.countries) && delivery.countries.includes(countryCode);
    } catch (error) {
      return false;
    } finally {
      clearTimeout(timeout);
    }
  };

  const submitLocalizationChange = async ({ countryCode, languageCode }) => {
    const startingPreference = JSON.stringify(getManualPreference());
    const startingManualChoice = hasManualChoice();
    if (countryCode && countryCode !== currentCountry && !(await countryHasDelivery(countryCode))) return;
    if (!(await cartIsEmpty()) || isRegionDialogOpen() || isShoppingActive()) return;
    if (JSON.stringify(getManualPreference()) !== startingPreference || hasManualChoice() !== startingManualChoice) return;
    const submittedLanguage = languageCode || (countryCode && countryCode !== currentCountry
      ? availableLanguages.get(currentLanguage) || '' : '');
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = config.localizationPath || '/localization';
    form.style.display = 'none';
    appendHiddenField(form, 'form_type', 'localization');
    appendHiddenField(form, 'utf8', '✓');
    appendHiddenField(form, '_method', 'put');
    if (countryCode) appendHiddenField(form, 'country_code', countryCode);
    if (submittedLanguage) appendHiddenField(form, 'language_code', submittedLanguage);
    appendHiddenField(form, 'return_to', getReturnTo(countryCode, submittedLanguage));
    document.body.appendChild(form);
    form.submit();
  };

  const getManualPreference = () => {
    if (!hasManualChoice()) return null;
    const savedCountry = String(getSavedValue(manualCountryKey) || '').toUpperCase();
    const countryCode = availableCountries.has(savedCountry) ? savedCountry : '';
    const languageCode = matchAvailableLanguage(getSavedValue(manualLanguageKey));
    return countryCode || languageCode ? { countryCode, languageCode } : null;
  };

  const needsCurrencyRefresh = (countryCode) => {
    const expectedCurrency = String(config.countryCurrencyMap?.[countryCode] || '').toUpperCase();
    return availableCountries.has(countryCode) && countryCode === currentCountry &&
      Boolean(expectedCurrency && currentCurrency && currentCurrency !== expectedCurrency);
  };

  const applyManualPreference = (preference) => {
    if (!preference) return false;
    const countryCode = preference.countryCode && (preference.countryCode !== currentCountry || needsCurrencyRefresh(preference.countryCode))
      ? preference.countryCode : '';
    const savedLanguage = hasExplicitLocalePath() ? '' : preference.languageCode;
    const languageCode = savedLanguage && normalizeLocaleCode(savedLanguage) !== currentLanguage ? savedLanguage : '';
    if (!countryCode && !languageCode) return true;
    const attemptedValue = `${preference.countryCode}:${savedLanguage || currentLanguage}`;
    if (getSessionValue(manualAttemptedKey) === attemptedValue) return true;
    setSessionValue(manualAttemptedKey, attemptedValue);
    if (getSessionValue(manualAttemptedKey) !== attemptedValue) return true;
    submitLocalizationChange({ countryCode, languageCode });
    return true;
  };

  const getSuggestedCountry = (payload) => {
    const detectedCountry = payload?.detected_values?.country?.handle;
    if (detectedCountry) return String(detectedCountry).toUpperCase();
    const suggestions = Array.isArray(payload?.suggestions) ? payload.suggestions : [];
    for (const suggestion of suggestions) {
      const countryCode = suggestion?.parts?.country?.handle || suggestion?.country?.handle ||
        suggestion?.country?.iso_code || suggestion?.country_code || suggestion?.iso_code;
      if (countryCode) return String(countryCode).toUpperCase();
    }
    return '';
  };

  const detectCountry = async () => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timeoutId;
    const timeout = new Promise((resolve) => {
      timeoutId = setTimeout(() => { controller?.abort(); resolve(null); }, 2000);
    });
    try {
      const request = fetch(config.endpoint || '/browsing_context_suggestions.json', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        ...(controller ? { signal: controller.signal } : {}),
      }).then((response) => response.ok ? response.json() : null);
      const suggestedCountry = getSuggestedCountry(await Promise.race([request, timeout]));
      return /^[A-Z]{2}$/.test(suggestedCountry) ? suggestedCountry : '';
    } catch (error) {
      return '';
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const runAutoLocalization = async () => {
    if (window.Shopify?.designMode || !currentCountry) return;
    if (availableCountries.size <= 1 && availableLanguages.size <= 1) return;
    if (window.location.pathname.includes('/challenge') || isRegionDialogOpen() || isShoppingActive()) return;
    if (!(await cartIsEmpty()) || isRegionDialogOpen() || isShoppingActive()) return;
    const preference = getManualPreference();
    if (applyManualPreference(preference) || getSessionValue(attemptedKey)) return;
    setSessionValue(attemptedKey, 'pending');
    if (!getSessionValue(attemptedKey)) return;
    const hadManualChoice = hasManualChoice();
    const detectedCountry = await detectCountry();
    const targetCountry = availableCountries.has(detectedCountry) ? detectedCountry : currentCountry;
    const targetLanguage = hasExplicitLocalePath() ? '' : preference?.languageCode || getCountryLanguage(detectedCountry);
    if (JSON.stringify(getManualPreference()) !== JSON.stringify(preference) || hadManualChoice !== hasManualChoice() ||
      isRegionDialogOpen() || isShoppingActive()) return;
    const countryCode = targetCountry !== currentCountry || needsCurrencyRefresh(targetCountry) ? targetCountry : '';
    const languageCode = targetLanguage && normalizeLocaleCode(targetLanguage) !== currentLanguage ? targetLanguage : '';
    setSessionValue(attemptedKey, `${targetCountry}:${targetLanguage || currentLanguage}`);
    if (countryCode || languageCode) submitLocalizationChange({ countryCode, languageCode });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAutoLocalization, { once: true });
  } else {
    runAutoLocalization();
  }
})();

